import { Box, IconButton } from "@mui/material";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { Draggable, DraggableProvidedDragHandleProps, Droppable } from "@hello-pangea/dnd";
import { useTranslation } from "@localization/useTranslation";
import { addSectionIcon, addSubsectionIcon } from "@common/icons";
import PolicyEditableItem from "./PolicyEditableItem";
import PolicySubsectionItem from "./PolicySubsectionItem";
import { PolicyControl, PolicySection, PolicySubsection } from "./PolicySections.types";

export interface PolicySectionItemProps {
    section: PolicySection;
    index: number;
    setFocusOnTitle: boolean;
    availableControls: Array<PolicyControl>;
    disabled: boolean;
    readOnlyPreview: boolean;
    dragHandleProps: DraggableProvidedDragHandleProps | null;
    onTitleChange: (title: string) => void;
    onContentChange: (content: string) => void;
    onLinkedControlsChange: (linkedControlIds: string[]) => void;
    onDelete?: () => void;
    createdSubsectionId: string | undefined;
    isAddingSubsection: boolean;
    isAddingSectionAfter: boolean;
    isAddingSectionAfterSubsection: boolean;
    onAddSubsection: (targetIndex: number) => void;
    onAddSectionAfter: () => void;
    onAddSectionAfterSubsection: (subsectionId: string) => void;
    onSubsectionTitleChange: (subsectionId: string, title: string) => void;
    onSubsectionContentChange: (subsectionId: string, content: string) => void;
    onSubsectionLinkedControlsChange: (subsectionId: string, linkedControlIds: string[]) => void;
    onSubsectionDelete: (subsection: PolicySubsection) => void;
    editingContentId: string | undefined;
    startEditingContent: (contentId: string) => void;
}
function PolicySectionItem(props: PolicySectionItemProps) {
    const { translateString } = useTranslation();
    const {
        section,
        index,
        setFocusOnTitle,
        availableControls,
        disabled,
        readOnlyPreview,
        dragHandleProps,
        onTitleChange,
        onContentChange,
        onLinkedControlsChange,
        onDelete,
        createdSubsectionId,
        isAddingSubsection,
        isAddingSectionAfter,
        isAddingSectionAfterSubsection,
        onAddSubsection,
        onAddSectionAfter,
        onAddSectionAfterSubsection,
        onSubsectionTitleChange,
        onSubsectionContentChange,
        onSubsectionLinkedControlsChange,
        onSubsectionDelete,
        editingContentId,
        startEditingContent,
    } = props;

    const subsections = [...section.subsections].sort((a, b) => a.order - b.order);

    return (
        <>
            <PolicyEditableItem
                numberLabel={`${index + 1}`}
                title={section.title}
                content={section.content}
                linkedControlIds={section.linkedControlIds}
                availableControls={availableControls}
                controlsHeaderKey="controlsRequiredBySection"
                setFocusOnTitle={setFocusOnTitle}
                disabled={disabled}
                readOnlyPreview={readOnlyPreview}
                dragHandleProps={dragHandleProps}
                titleFontSize={20}
                onTitleChange={onTitleChange}
                onContentChange={onContentChange}
                onLinkedControlsChange={onLinkedControlsChange}
                onDelete={onDelete}
                id={section.id}
                isEditingContent={editingContentId === section.id}
                onStartEditingContent={() => startEditingContent(section.id)}
                extraActions={
                    !disabled && (
                        <>
                            <DotLegalTooltip text={translateString("addSection")}>
                                <span>
                                    <IconButton size="small" onClick={onAddSectionAfter} disabled={isAddingSectionAfter} aria-label="add section after">
                                        {addSectionIcon}
                                    </IconButton>
                                </span>
                            </DotLegalTooltip>
                            <DotLegalTooltip text={translateString("addSubsection")}>
                                <span>
                                    <IconButton size="small" onClick={() => onAddSubsection(0)} disabled={isAddingSubsection} aria-label="add subsection">
                                        {addSubsectionIcon}
                                    </IconButton>
                                </span>
                            </DotLegalTooltip>
                        </>
                    )
                }
            />

            {subsections.length > 0 && (
                <Box sx={{ paddingTop: 2, paddingBottom: 1 }}>
                    <Droppable droppableId={`policy-subsections-${section.id}`} type="SUBSECTION">
                        {(provided, snapshot) => (
                            <Box
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                sx={(theme) => ({ background: snapshot.isDraggingOver ? theme.palette.grey.A100 : "none" })}
                            >
                                {subsections.map((subsection, subIndex) => (
                                    <Draggable
                                        key={subsection.id}
                                        draggableId={subsection.id}
                                        index={subIndex}
                                        isDragDisabled={disabled || subsections.length < 2}
                                    >
                                        {(draggableProvided, draggableSnapshot) => (
                                            <Box ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                                                <Box
                                                    sx={(theme) => ({
                                                        ...(draggableSnapshot.isDragging && {
                                                            backgroundColor: theme.palette.background.paper,
                                                            boxShadow: "0px 3px 10px #E7E7E7",
                                                            borderRadius: "20px",
                                                            overflow: "hidden",
                                                            paddingX: 3,
                                                            paddingTop: 1,
                                                            paddingBottom: 3,
                                                            "& > div": {
                                                                marginLeft: "0 !important",
                                                                marginRight: "0 !important",
                                                                marginBottom: "0 !important",
                                                                paddingLeft: "0 !important",
                                                                paddingRight: "0 !important",
                                                            },
                                                        }),
                                                    })}
                                                >
                                                    <PolicySubsectionItem
                                                        subsection={subsection}
                                                        sectionIndex={index}
                                                        subIndex={subIndex}
                                                        setFocusOnTitle={createdSubsectionId === subsection.id}
                                                        availableControls={availableControls}
                                                        disabled={disabled}
                                                        readOnlyPreview={readOnlyPreview}
                                                        dragHandleProps={!disabled && subsections.length > 1 ? draggableProvided.dragHandleProps : null}
                                                        onTitleChange={(title) => onSubsectionTitleChange(subsection.id, title)}
                                                        onContentChange={(content) => onSubsectionContentChange(subsection.id, content)}
                                                        onLinkedControlsChange={(ids) => onSubsectionLinkedControlsChange(subsection.id, ids)}
                                                        onDelete={() => onSubsectionDelete(subsection)}
                                                        onAddAfter={() => onAddSubsection(subIndex + 1)}
                                                        onAddSectionAfter={() => onAddSectionAfterSubsection(subsection.id)}
                                                        isAdding={isAddingSubsection}
                                                        isAddingSectionAfter={isAddingSectionAfterSubsection}
                                                        id={subsection.id}
                                                        isEditingContent={editingContentId === subsection.id}
                                                        onStartEditingContent={() => startEditingContent(subsection.id)}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </Box>
            )}
        </>
    );
}

export default PolicySectionItem;
